import { resolveToParts } from './resolve.ts';
import type { ResolveOptions } from './types.ts';

// Inline elements a message's markup may open, keyed by MF2 markup name.
// Extend by passing `tags` to renderMessage.
const DEFAULT_TAGS: Record<string, string> = { strong: 'strong', em: 'em', code: 'code' };

// How a caller turns a message's parts into its view library's nodes. Kept
// framework-neutral (no import of Preact/React) so this package stays usable
// server-side. Callers implement these with their own `h`/`createElement`,
// which keeps the element creation strongly typed — this module never asserts.
export interface MessageRenderer<Node> {
  // A literal run of text.
  text: (value: string) => Node;
  // Wrap children in the element for a mapped markup tag (e.g. "strong").
  element: (tag: string, children: Node[]) => Node;
  // Combine sibling nodes into one (the whole result, and any markup whose
  // name isn't in the tag map).
  fragment: (children: Node[]) => Node;
}

export interface RenderMessageOptions<Node> extends ResolveOptions {
  render: MessageRenderer<Node>;
  // Overrides/extends the markup-name → tag map (defaults: strong, em, code).
  tags?: Record<string, string>;
}

// Resolve a message and render its inline MF2 markup as real nodes: text runs
// go through `render.text`, each `{#name}…{/name}` whose name maps to a tag
// goes through `render.element`, and the whole thing is combined with
// `render.fragment`. Nesting is supported; unmapped markup falls back to a
// fragment of its children.
export function renderMessage<Node>(key: string, options: RenderMessageOptions<Node>): Node {
  const { render, tags } = options;
  const tagMap = tags ? { ...DEFAULT_TAGS, ...tags } : DEFAULT_TAGS;
  const parts = resolveToParts(key, options);

  let cursor = 0;
  const build = (): Node[] => {
    const children: Node[] = [];
    while (cursor < parts.length) {
      const part = parts[cursor++];
      if (part.type === 'text') {
        children.push(render.text(part.value));
      } else if (part.kind === 'open') {
        const tag = tagMap[part.name];
        const inner = build();
        children.push(tag ? render.element(tag, inner) : render.fragment(inner));
      } else if (part.kind === 'close') {
        break;
      }
      // 'standalone' markup: no catalog message uses it — ignored.
    }
    return children;
  };

  return render.fragment(build());
}
